import type { ReactNode } from "react";
import { themeClasses, type Theme } from "@cw/design-tokens/theme";
import { ContainedSection, SectionHeader } from "../primitives/Section";

export interface ComparisonColumn { label: string; highlight?: boolean; sub?: string }
export interface ComparisonRow { label: string; note?: string; cells: (boolean | string)[] }

function Cell({ v, highlight }: { v: boolean | string; highlight?: boolean }) {
  if (v === true) return <span aria-label="Included" className={`inline-flex size-6 items-center justify-center rounded-full ${highlight ? "bg-signal text-ink" : "bg-accent-soft text-accent-ink"}`}><svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5l3 3 7-7" /></svg></span>;
  if (v === false) return <span aria-label="Not included" className="text-hairline">—</span>;
  return <span className="text-sm">{v}</span>;
}

/** Generic matrix. BundleMatrix (PSCX) is built on this. */
export function ComparisonTable({ eyebrow, heading, intro, columns, rows, footnote, theme = "paper", id, caption }: { eyebrow?: string; heading?: string; intro?: ReactNode; columns: ComparisonColumn[]; rows: ComparisonRow[]; footnote?: ReactNode; theme?: Theme; id?: string; caption: string }) {
  const t = themeClasses(theme);
  return (
    <ContainedSection theme={theme} id={id}>
      <SectionHeader eyebrow={eyebrow} heading={heading} intro={intro} theme={theme} />
      <div className={`overflow-x-auto rounded-card ${t.card} shadow-card`}>
        <table className="w-full min-w-[40rem] border-collapse text-left">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className={`border-b ${t.border}`}>
              <th scope="col" className="p-5 text-xs font-semibold uppercase tracking-[0.14em] text-slate">Module</th>
              {columns.map((c) => (
                <th key={c.label} scope="col" className={`p-5 text-center align-bottom ${c.highlight ? "bg-signal-soft/60" : ""}`}>
                  <span className={`${t.heading} block font-display text-lg font-semibold`}>{c.label}</span>
                  {c.sub ? <span className="mt-1 block text-xs font-normal text-slate">{c.sub}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={`border-b last:border-0 ${t.border}`}>
                <th scope="row" className={`${t.heading} p-5 text-[15px] font-medium`}>{r.label}{r.note ? <span className="mt-0.5 block text-xs font-normal text-slate">{r.note}</span> : null}</th>
                {r.cells.map((v, i) => <td key={i} className={`p-5 text-center ${columns[i]?.highlight ? "bg-signal-soft/60" : ""}`}><Cell v={v} highlight={columns[i]?.highlight} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footnote ? <p className={`${t.muted} mt-4 text-sm`}>{footnote}</p> : null}
    </ContainedSection>
  );
}
